/* eslint-disable no-restricted-globals */
import axios from "axios";
import dayjs from "dayjs";

import { CognitoJwtVerifier } from "aws-jwt-verify";
import { User } from "../components/Authentication";
import { Demo, DemoVariant } from "./types/demo";
import { logger } from "./LogUtils";

const idTokenVerifier = CognitoJwtVerifier.create({
  userPoolId: import.meta.env.REACT_APP_POOL_ID,
  clientId: import.meta.env.REACT_APP_CLIENT_ID,
  tokenUse: "id",
});

/**
 * Checks to see if this is local mode or not
 */
export function isLocalMode() {
  return import.meta.env.REACT_APP_IS_LOCAL === "true";
}

/**
 * Checks to see if this is local mode or not
 */
export function getStage(): string | undefined {
  return import.meta.env.REACT_APP_STAGE;
}

/**
 * Stores the identity on the client only available in local mode
 */
export async function storeIdentity(idToken: string, source: string) {
  try {
    logger.info(
      `AuthUtils.storeIdentity(${source}) storing identity in localstore`
    );

    localStorage.removeItem("identity");

    // Check this is a valid JWT id token
    const idTokenPayload = await idTokenVerifier.verify(idToken);

    localStorage.setItem("identity", idToken);

    logger.info(
      `AuthUtils.storeIdentity(${source}) stored identity in localstore`
    );

    return idTokenPayload;
  } catch (error) {
    logger.error(
      `AuthUtils.storeIdentity(${source}) invalid id token received`,
      error
    );
    throw error;
  }
}

/**
 * Helper functions to calculate a user from an identity token
 */
export function calculateUser(identityToken: any): User {
  const user: User = {
    email: identityToken.email,
    userId: undefined,
    firstName: identityToken.given_name,
    lastName: identityToken.family_name,
    displayName: undefined,
    roleName: undefined,
    country: undefined,
    city: undefined,
    admin: false,
    builder: false,
    partner: false,
    partnerAdmin: false,
    tester: false,
  };

  if (identityToken["cognito:groups"] !== undefined) {
    if (identityToken["cognito:groups"].includes("Admin")) {
      user.admin = true;
      user.builder = true;
      user.partnerAdmin = true;
    }

    if (identityToken["cognito:groups"].includes("Builder")) {
      user.builder = true;
    }

    if (identityToken["cognito:groups"].includes("Partner")) {
      user.partner = true;
    }

    if (identityToken["cognito:groups"].includes("PartnerAdmin")) {
      user.partnerAdmin = true;
    }

    if (identityToken["cognito:groups"].includes("Tester")) {
      user.tester = true;
    }
  }

  if (identityToken["custom:roleName"] !== undefined) {
    user.roleName = identityToken["custom:roleName"];
  }

  if (identityToken["custom:country"] !== undefined) {
    user.country = identityToken["custom:country"];
  }

  if (identityToken["custom:city"] !== undefined) {
    user.city = identityToken["custom:city"];
  }

  if (identityToken["custom:displayName"] !== undefined) {
    user.displayName = identityToken["custom:displayName"];
  }

  if (identityToken["custom:userId"] !== undefined) {
    user.userId = identityToken["custom:userId"];
  }

  return user;
}

/**
 * Attempts to refresh the token returning the identity
 */
export async function refreshToken(): Promise<string> {
  try {
    const options = {
      withCredentials: true,
    };

    const url = `${import.meta.env.REACT_APP_API_BASE}/api/refresh_token`;

    logger.info(`AuthUtils.refreshToken() about to refresh tokens`);

    const refreshResponse = await axios.get(url, options);

    if (refreshResponse.data.success !== true) {
      logger.info(
        `AuthUtils.refreshToken() failed to refresh tokens: ${refreshResponse.data.cause}`
      );
      throw new Error(refreshResponse.data.cause);
    } else {
      logger.info(`AuthUtils.refreshToken() successfully refreshed tokens`);
      return refreshResponse.data.identity;
    }
  } catch (error: any) {
    logger.error(
      `AuthUtils.refreshToken() failed to refresh token: ${error.message}`
    );
    throw error;
  }
}

/**
 * If the id token is not defined login in, otherwise parse the token and verify it.
 * If its still valid extract from data from it, otherwise try a refresh
 * Returns a state object with the token the extracted user information
 *
 * {
 *  loggedIn: [true]
 *  navigateHome: [true]
 *  logOut: [true]
 *  user: { user details }
 * }
 */
export async function loadIdentity(identityInbound?: string) {
  try {
    logger.info(`AuthUtils.loadIdentity() loading identity`);
    var navigateHome = false;

    // If we got an identity on the url, validate it and store it
    if (identityInbound !== undefined && identityInbound !== null) {
      const identityInboundPayload = await storeIdentity(
        identityInbound,
        "URL"
      );
      return {
        loggedIn: true,
        navigateHome: true,
        user: calculateUser(identityInboundPayload),
      };
    }

    // Otherwise try and fetch it from local store
    var identityToken = localStorage.getItem("identity");

    if (identityToken !== null) {
      // Check this is a valid JWT id token
      var identityTokenPayload: any = undefined;
      var refreshRequired = false;

      try {
        identityTokenPayload = await idTokenVerifier.verify(identityToken);
        const identityExpiry = dayjs(identityTokenPayload.exp * 1000);
        const secondsUntilExpiry = identityExpiry.diff(dayjs(), "seconds");
        logger.info(
          `AuthUtils.loadIdentity() identity token expires in: ${secondsUntilExpiry}`
        );

        if (secondsUntilExpiry < 120) {
          logger.info(
            `AuthUtils.loadIdentity() identity token expires within 2 minutes forcing a refresh`
          );
          refreshRequired = true;
        }
      } catch (tokenError: any) {
        // Handle token expiry errors
        if (tokenError.message.startsWith("Token expired")) {
          logger.warn(
            `AuthUtils.loadIdentity() detected expired identity token, requesting refresh: ${tokenError.message}`
          );
          refreshRequired = true;
        }
        // Other errors should cause a log out
        else {
          throw tokenError;
        }
      }

      // Check for token expiration and refresh
      if (refreshRequired) {
        identityToken = await refreshToken();
        identityTokenPayload = await storeIdentity(identityToken, "refresh");
        logger.info(`AuthUtils.loadIdentity() token refresh was successful`);
      } else {
        logger.info(`AuthUtils.loadIdentity() no token refresh was required`);
      }

      return {
        loggedIn: true,
        navigateHome: navigateHome,
        user: calculateUser(identityTokenPayload),
      };
    } else {
      logger.info(`AuthUtils.loadIdentity() user is not logged in`);
      return {
        loggedIn: false,
      };
    }
  } catch (error: any) {
    logger.error(
      `AuthUtils.getIdentity() failed to load identity: ${error.message}`
    );
    return undefined;
  }
}

export function getAmazonUserId(email: string): string | undefined {
  const atIndex = email.indexOf("@");
  const includesAmazon = email.includes("@amazon.");

  if (includesAmazon) {
    return email.substring(0, atIndex);
  }

  return undefined;
}

/**
 * AWS Sellers have no specific grouop to check,
 * internal users are all non-partners
 */
export const isInternalUser = (user: User) => {
  return !user.partner;
};

/**
 * A user is a partner if they have the partner flag
 */
export const isPartnerUser = (user: User) => {
  return user.partner;
};

export const isAdminUser = (user: User) => {
  return user.admin;
};

export const isBuilderUser = (user: User) => {
  return user.builder || user.admin;
};

export const isPartnerAdminUser = (user: User) => {
  return user.partnerAdmin || user.admin;
};

export const isTesterUser = (user: User) => {
  return user.tester || user.admin;
};

export const isDemoOwner = (user: User, demo: Demo) => {
  return user.admin || (user.builder && demo.owners.includes(user.email));
};

export const isVariantOwner = (user: User, variant: DemoVariant) => {
  return (
    variant !== undefined &&
    (user.admin || (user.builder && variant.owners.includes(user.email)))
  );
};

/**
 * Saves a redirect url for post login redirect into session storage
 */
export const saveRedirectUrl = () => {
  // Don't save the login page itself as a redirect
  if (location.pathname !== "/login") {
    logger.info(`Saving login url: ${location.pathname}${location.search}`);
    sessionStorage.setItem(
      "REDIRECT",
      `${location.pathname}${location.search}`
    );
  }
};

/**
 * Clears the pre-login redirect url from sessin storage
 */
export const clearRedirectUrl = () => {
  logger.info("Cleared redirect URL");
  sessionStorage.removeItem("REDIRECT");
};

/**
 * Fetches the redirect url from session storage
 */
export const getRedirectUrl = (): string => {
  const url = sessionStorage.getItem("REDIRECT");

  if (url) {
    return url;
  } else {
    return "/";
  }
};

/**
 * Redirect to login, optionally saving the redirect url
 */
export const redirectToLogin = (saveUrl: boolean) => {
  logger.info(`Redirecting to login`);
  if (saveUrl) {
    saveRedirectUrl();
  } else {
    clearRedirectUrl();
  }

  window.location.href = "/login";
};
